import { STEP_HEIGHT, penetrates, supportHeight } from '../core/collision.js';
import { EVENT } from './events.js';

/**
 * Getting off the floor.
 *
 * There is no list of climbable objects in this file and there is no climb
 * point anywhere in the level data. The building is a pile of axis-aligned
 * boxes; a box has a top; a top at the right height in front of you is a thing
 * you can get your hands on. That is the whole rule, and it is why a tree, a
 * scaffold tower, the dock rail and a stack of crates all work without any of
 * them being mentioned here or knowing anything about climbing.
 *
 * What the height decides:
 *
 *   up to a step        walking already carries you up; not a climb
 *   step to chest       MANTLE — you pull straight up onto it
 *   chest to fingertips HANG — you catch the edge and hang off it
 *   above fingertips    nothing, from here
 *
 * "From here" is doing the work in that last line. The bands are measured from
 * where your hands are *now*, and your hands go up when you jump and when you
 * are standing on something you already climbed. So a scaffold is climbed in
 * stages, a crate gets you onto the stack beside it, and a jump catches an edge
 * a standing reach misses — none of which is written down as a case, and none
 * of which needed a designer to place anything.
 *
 * Two tests keep it honest. The edge has to be in front of you, so you do not
 * peel off a wall you happen to be standing against. And the top has to have
 * room for a body on it, so the fact that a wall also has a top does not make
 * a wall a ladder.
 */

/** Where your feet hang when your hands are on an edge. */
const HANG_DROP = 1.75;

/**
 * Metres above your feet: what you pull straight up onto, and what you can
 * reach at all.
 *
 * The mantle band ends exactly where a hang becomes possible, and it is the
 * same number rather than a second guess at one, because a hang is your hands
 * on the edge and your body under it — an edge lower than a hanging body is an
 * edge you cannot hang from. Pick the two independently and there is a band in
 * between where the game says "hang" and then puts your feet through the floor,
 * which is what a five foot crate did.
 *
 * So: anything from a step up to a body's hanging height you scramble over,
 * anything from there to your fingertips you catch and hang off.
 */
const MANTLE_MAX = HANG_DROP;
const HANG_MAX = 2.25;

/** How far in front the edge may be. An arm, not a grapple. */
const REACH_OUT = 0.75;

/** Cosine of the half-angle you have to be facing the edge within. */
const FACING = 0.45;

/** Seconds a pull-up takes. Long enough to be a commitment, short enough to use. */
const MANTLE_SECONDS = 0.45;

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

export function createClimbing({ world, bus, tuning }) {
  let state = 'free';
  /** The edge currently under your hands, or being pulled over. */
  let held = null;
  let progress = 0;
  let from = null;

  /**
   * The best edge in front of the player, and what you could do with it.
   *
   * `feet` is passed rather than read so that the same function answers "what
   * can I reach standing here" and "what could I reach at the top of a jump".
   */
  function find(position, forward, feet) {
    let best = null;
    const ahead = {
      x: position.x + forward.x * REACH_OUT,
      z: position.z + forward.z * REACH_OUT,
    };
    for (const box of world.near(ahead.x, ahead.z, REACH_OUT + tuning.radius)) {
      const top = box.max.y;
      const rise = top - feet;
      if (rise <= STEP_HEIGHT || rise > HANG_MAX) continue;

      const ex = clamp(position.x, box.min.x, box.max.x);
      const ez = clamp(position.z, box.min.z, box.max.z);
      const dx = ex - position.x;
      const dz = ez - position.z;
      const gap = Math.hypot(dx, dz);
      if (gap > REACH_OUT + tuning.radius) continue;
      // Standing against it counts as facing it; there is no direction to be
      // off by when the gap is nothing.
      if (gap > 0.05 && (dx / gap) * forward.x + (dz / gap) * forward.z < FACING) continue;

      const stand = standingSpot(box, ex, ez);
      if (!stand) continue;

      // Nearer wins, and a low edge beats a high one at the same distance: you
      // step onto the crate in front of the shelf rather than swinging off the
      // shelf above it.
      const score = gap + rise * 0.35;
      if (best && score >= best.score) continue;
      best = {
        score,
        kind: rise <= MANTLE_MAX ? 'mantle' : 'hang',
        rise,
        top,
        box,
        edge: { x: ex, z: ez },
        stand,
      };
    }
    return best;
  }

  /**
   * Somewhere on top of the box to put a body, or null if there is nowhere.
   *
   * A wall has a top too. What a wall does not have is a foot of air above it
   * and room for a pair of shoulders, and that — rather than a flag on the
   * data — is what stops you climbing the building.
   */
  function standingSpot(box, ex, ez) {
    const inset = tuning.radius + 0.05;
    const midX = (box.min.x + box.max.x) / 2;
    const midZ = (box.min.z + box.max.z) / 2;
    const x = clamp(ex + Math.sign(midX - ex) * inset, box.min.x, box.max.x);
    const z = clamp(ez + Math.sign(midZ - ez) * inset, box.min.z, box.max.z);
    const top = box.max.y;
    // Room for the whole body, not a number picked to be generous. It is why a
    // seven foot shelf in an eleven foot room is furniture rather than a perch,
    // and why almost every hang in the building is outdoors.
    if (penetrates(x, z, tuning.radius, top + 0.02, top + tuning.height, world)) return null;
    // And it has to actually be the thing you land on, not a lip with a deeper
    // hole behind it.
    if (supportHeight(x, z, tuning.radius, top + 0.02, world) < top - 0.02) return null;
    return { x, z, y: top };
  }

  function noise(cause, position, radius) {
    bus?.emit(EVENT.NOISE, { actor: 'player', cause, position: { ...position }, radius });
  }

  return {
    find,

    get state() {
      return state;
    },
    /** True while climbing owns the body and ordinary walking must not run. */
    get busy() {
      return state !== 'free';
    },
    /**
     * How far through a pull-up, 0 to 1. Zero at any other time.
     *
     * Read-only, and it is the number `update` was already keeping — a mantle
     * is two motions, up and then over, and this says which one is happening.
     */
    get progress() {
      return state === 'mantling' ? progress : 0;
    },
    get edge() {
      return held;
    },

    /** Catches an edge. A mantle goes straight over it; a hang stops on it. */
    grab(found, position) {
      if (state !== 'free' || !found) return false;
      held = found;
      if (found.kind === 'hang') {
        state = 'hanging';
        noise('climb', { x: found.edge.x, y: found.top, z: found.edge.z }, 4);
        return true;
      }
      state = 'mantling';
      progress = 0;
      from = { ...position };
      noise('climb', { x: found.edge.x, y: found.top, z: found.edge.z }, 6);
      return true;
    },

    /** Pulls up over an edge you are hanging from. */
    mantle(position) {
      if (state !== 'hanging' || !held) return false;
      state = 'mantling';
      progress = 0;
      from = { ...position };
      noise('climb', { x: held.edge.x, y: held.top, z: held.edge.z }, 6);
      return true;
    },

    /** Lets go. You fall from wherever you are; nothing catches you. */
    release() {
      if (state === 'free') return false;
      state = 'free';
      held = null;
      from = null;
      return true;
    },

    /**
     * Moves the body while climbing owns it. Returns the position it should be
     * at, or null when the player is free and their own physics apply.
     */
    update(dt, position) {
      if (state === 'hanging' && held) {
        return { x: held.edge.x, y: held.top - HANG_DROP, z: held.edge.z, grounded: false };
      }
      if (state === 'mantling' && held) {
        progress = Math.min(1, progress + dt / MANTLE_SECONDS);
        const to = held.stand;
        // Up first, then over. Going diagonally puts your shoulders through the
        // lip of whatever you are climbing.
        const rise = Math.min(1, progress / 0.6);
        const over = Math.max(0, (progress - 0.4) / 0.6);
        const startY = from?.y ?? position.y;
        const at = {
          x: (from?.x ?? position.x) + (to.x - (from?.x ?? position.x)) * over,
          y: startY + (to.y - startY) * rise,
          z: (from?.z ?? position.z) + (to.z - (from?.z ?? position.z)) * over,
          grounded: false,
        };
        if (progress >= 1) {
          state = 'free';
          held = null;
          from = null;
          return { x: to.x, y: to.y, z: to.z, grounded: true };
        }
        return at;
      }
      return null;
    },

    reset() {
      state = 'free';
      held = null;
      from = null;
      progress = 0;
    },
  };
}
